import React from 'react';

const PaginationControls = ({
  currentPage,
  totalPages,
  pageSize,
  totalRecords,
  pageSizeOptions = [10, 50, 100, 500, 1000], // Updated options
  onPageChange,
  onPageSizeChange,
  isLoading = false, // New prop to disable controls during loading
  className = '',
  uiConfig = {
    container: 'flex flex-col items-center w-full',
    paginationContainer: 'flex md:gap-10 sm:gap-4 justify-center items-center bg-white p-1 text-center rounded-md mb-1 shadow-md w-full',
    paginationButton: 'p-2 rounded-md',
    paginationButtonActive: 'hover:bg-blue/90 hover:text-white cursor-pointer',
    paginationButtonDisabled: 'cursor-not-allowed opacity-50 bg-gray-200',
    pageSelect: 'cursor-pointer',
    pageSizeSelect: 'cursor-pointer',
    recordsInfo: 'text-sm'
  }
}) => {
  const getPageNumbers = () => {
    return Array.from({ length: Math.max(1, totalPages) }, (_, i) => i + 1);
  };

  // Helper function to get tooltip messages for disabled buttons
  const getTooltipMessage = (buttonType) => {
    if (isLoading) return "Loading data, please wait...";
    if (totalPages <= 1) return "Only one page available";
    
    switch(buttonType) {
      case 'first':
      case 'prev':
        return currentPage <= 1 ? "Already on first page" : "";
      case 'next':
      case 'last':
        return currentPage >= totalPages ? "Already on last page" : "";
      default:
        return "";
    }
  };

  // Helper function to handle page change with validation
  const handlePageChange = (newPage, buttonType) => {
    // Don't allow changes during loading
    if (isLoading) return;
    
    // Validate page bounds
    if (newPage < 1 || newPage > totalPages) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`Invalid page change attempt: ${newPage} (valid range: 1-${totalPages})`);
      }
      return;
    }
    
    // Don't change if already on the target page
    if (newPage === currentPage) {
      return;
    }
    
    onPageChange(newPage);
  };

  // Helper function to handle page size change
  const handlePageSizeChange = (newPageSize) => {
    // Don't allow changes during loading
    if (isLoading) return;
    
    onPageSizeChange(newPageSize);
  };

  if (totalPages <= 0) return null;

  // Show pagination controls even for single page to provide user feedback
  const isSinglePage = totalPages === 1;
  const isDisabled = isLoading || isSinglePage;

  return (
    <div className={`${uiConfig.container} ${className}`} style={{ position: 'relative', zIndex: 1 }}>
      {/* Pagination Controls - Exact SLG styling */}
      <div className={uiConfig.paginationContainer} style={{ position: 'relative', zIndex: 10 }}>
        {/* First Page */}
        <button
          type="button"
          title={getTooltipMessage('first')}
          disabled={currentPage <= 1 || isDisabled}
          className={`${uiConfig.paginationButton} ${
            (currentPage > 1 && !isDisabled) ? uiConfig.paginationButtonActive : uiConfig.paginationButtonDisabled
          }`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isDisabled) {
              handlePageChange(1, 'first');
            }
          }}
        >
          {"<<"}
        </button>
        
        {/* Previous Page */}
        <button
          type="button"
          title={getTooltipMessage('prev')}
          disabled={currentPage <= 1 || isDisabled}
          className={`${uiConfig.paginationButton} ${
            (currentPage > 1 && !isDisabled) ? uiConfig.paginationButtonActive : uiConfig.paginationButtonDisabled
          }`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isDisabled) {
              handlePageChange(currentPage - 1, 'prev');
            }
          }}
        >
          {"<"}
        </button>
        
        {/* Page Select */}
        <select 
          value={currentPage} 
          onChange={(e) => {
            e.preventDefault();
            e.stopPropagation();
            const newPage = parseInt(e.target.value, 10);
            handlePageChange(newPage, 'select');
          }}
          disabled={isLoading}
          className={`${uiConfig.pageSelect} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ pointerEvents: isLoading ? 'none' : 'auto', zIndex: 10 }}
          title={isLoading ? "Loading data, please wait..." : `Current page: ${currentPage} of ${totalPages}`}
        >
          {getPageNumbers().map((pageNumber) => (
            <option key={pageNumber} value={pageNumber}>
              Page {pageNumber}
            </option>
          ))}
        </select>
        
        {/* Page Size Select */}
        <select
          value={pageSize}
          onChange={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePageSizeChange(Number(e.target.value));
          }}
          disabled={isLoading}
          className={`${uiConfig.pageSizeSelect} ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ pointerEvents: isLoading ? 'none' : 'auto', zIndex: 10 }}
          title={isLoading ? "Loading data, please wait..." : `Items per page: ${pageSize}`}
        >
          {pageSizeOptions.map((size) => (
            <option key={size} value={size}>
              {size} per page
            </option>
          ))}
        </select>
        
        {/* Next Page */}
        <button
          type="button"
          title={getTooltipMessage('next')}
          disabled={currentPage >= totalPages || isDisabled}
          className={`${uiConfig.paginationButton} ${
            (currentPage < totalPages && !isDisabled) ? uiConfig.paginationButtonActive : uiConfig.paginationButtonDisabled
          }`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isDisabled) {
              handlePageChange(currentPage + 1, 'next');
            }
          }}
        >
          {">"}
        </button>
        
        {/* Last Page */}
        <button
          type="button"
          title={getTooltipMessage('last')}
          disabled={currentPage >= totalPages || isDisabled}
          className={`${uiConfig.paginationButton} ${
            (currentPage < totalPages && !isDisabled) ? uiConfig.paginationButtonActive : uiConfig.paginationButtonDisabled
          }`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isDisabled) {
              handlePageChange(totalPages, 'last');
            }
          }}
        >
          {">>"}
        </button>
      </div>
      
      {/* Records Info */}
      <p className={uiConfig.recordsInfo}>
        {isLoading ? (
          <span className="flex items-center gap-2">
            <span className="loading loading-spinner loading-sm"></span>
            Loading data...
          </span>
        ) : (
          `Showing page ${currentPage} of ${totalPages} (${totalRecords} records)`
        )}
      </p>
    </div>
  );
};

export default PaginationControls;